import { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "wouter";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  type CpdModule,
  type CpdModuleProgress,
  type ModuleSection,
  type QuizQuestion,
  useGetModule,
  getGetModuleQueryKey,
  useGetMyModuleProgress,
  getGetMyModuleProgressQueryKey,
  useUpdateModuleProgress,
} from "@workspace/api-client-react";
import {
  BookOpenCheck,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Circle,
  Clock,
  Award,
  Brain,
  PenLine,
  BookOpen,
  Star,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

const difficultyConfig = {
  foundation: { label: "Foundation", class: "bg-green-50 text-green-700 border-green-200" },
  intermediate: { label: "Intermediate", class: "bg-amber-50 text-amber-700 border-amber-200" },
  advanced: { label: "Advanced", class: "bg-purple-50 text-purple-700 border-purple-200" },
};

const sectionTypeConfig = {
  reading: { icon: BookOpen, label: "Reading", color: "text-teal-600" },
  quiz: { icon: Brain, label: "Quiz", color: "text-indigo-600" },
  reflection: { icon: PenLine, label: "Reflection", color: "text-amber-600" },
};

function SectionIcon({ type }: { type: ModuleSection["type"] }) {
  const cfg = sectionTypeConfig[type];
  return <cfg.icon className={`h-4 w-4 ${cfg.color}`} />;
}

function renderInlineMarkdown(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

function ReadingContent({ content }: { content: string }) {
  const lines = content.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("# ")) {
      elements.push(<h1 key={i} className="text-xl font-bold text-slate-900 mt-6 mb-3">{line.slice(2)}</h1>);
    } else if (line.startsWith("## ")) {
      elements.push(<h2 key={i} className="text-lg font-bold text-slate-900 mt-5 mb-2">{line.slice(3)}</h2>);
    } else if (line.startsWith("### ")) {
      elements.push(<h3 key={i} className="text-base font-bold text-slate-900 mt-4 mb-2">{line.slice(4)}</h3>);
    } else if (line.startsWith("| ")) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      const [headerRow, , ...dataRows] = tableLines;
      const headers = headerRow.split("|").map(c => c.trim()).filter(Boolean);
      elements.push(
        <div key={i} className="overflow-x-auto mb-4">
          <table className="w-full text-sm border-collapse border border-slate-200 rounded-lg overflow-hidden">
            <thead className="bg-slate-50">
              <tr>
                {headers.map((h, hi) => (
                  <th key={hi} className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-800">
                    {renderInlineMarkdown(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, ri) => {
                const cells = row.split("|").map(c => c.trim()).filter(Boolean);
                return (
                  <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                    {cells.map((cell, ci) => (
                      <td key={ci} className="border border-slate-200 px-3 py-2 text-slate-700">
                        {renderInlineMarkdown(cell)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
      continue;
    } else if (line.match(/^\d+\. /)) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].match(/^\d+\. /)) {
        listItems.push(lines[i].replace(/^\d+\. /, ""));
        i++;
      }
      elements.push(
        <ol key={i} className="list-decimal pl-5 space-y-1 mb-3">
          {listItems.map((item, li) => (
            <li key={li} className="text-slate-700 text-sm leading-relaxed">
              {renderInlineMarkdown(item)}
            </li>
          ))}
        </ol>
      );
      continue;
    } else if (line.startsWith("- ")) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        listItems.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={i} className="list-disc pl-5 space-y-1 mb-3">
          {listItems.map((item, li) => (
            <li key={li} className="text-slate-700 text-sm leading-relaxed">
              {renderInlineMarkdown(item)}
            </li>
          ))}
        </ul>
      );
      continue;
    } else if (line.trim() === "") {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(
        <p key={i} className="text-slate-700 text-sm leading-relaxed mb-2">
          {renderInlineMarkdown(line)}
        </p>
      );
    }
    i++;
  }

  return <div className="mb-8">{elements}</div>;
}

function QuizView({
  questions,
  onAllAnswered,
}: {
  questions: QuizQuestion[];
  onAllAnswered: () => void;
}) {
  const [answers, setAnswers] = useState<Record<number, number | null>>({});
  const [submitted, setSubmitted] = useState<Record<number, boolean>>({});

  const handleSelect = (qId: number, idx: number) => {
    if (submitted[qId]) return;
    setAnswers((prev) => ({ ...prev, [qId]: idx }));
  };

  const handleSubmit = (qId: number) => {
    if (answers[qId] === undefined || answers[qId] === null) return;
    const newSubmitted = { ...submitted, [qId]: true };
    setSubmitted(newSubmitted);
    const allAnswered = questions.every((q) => newSubmitted[q.id]);
    if (allAnswered) onAllAnswered();
  };

  return (
    <div className="space-y-8">
      {questions.map((q, qi) => {
        const selected = answers[q.id];
        const isSubmitted = submitted[q.id];
        const isCorrect = selected === q.correctIndex;

        return (
          <div key={q.id} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
            <p className="font-semibold text-slate-900 mb-4">
              <span className="text-teal-600 mr-2">Q{qi + 1}.</span>
              {q.question}
            </p>
            <div className="space-y-2 mb-4">
              {q.options.map((opt, idx) => {
                let optClass = "w-full text-left px-4 py-3 rounded-lg border text-sm transition-all ";
                if (!isSubmitted) {
                  optClass += selected === idx
                    ? "border-teal-500 bg-teal-50 text-teal-900 font-medium"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 text-slate-700";
                } else {
                  if (idx === q.correctIndex) {
                    optClass += "border-green-500 bg-green-50 text-green-900 font-medium";
                  } else if (selected === idx) {
                    optClass += "border-red-400 bg-red-50 text-red-900";
                  } else {
                    optClass += "border-slate-200 bg-white text-slate-500";
                  }
                }
                return (
                  <button key={idx} className={optClass} onClick={() => handleSelect(q.id, idx)}>
                    <span className="flex items-center gap-2">
                      {isSubmitted && idx === q.correctIndex && (
                        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                      )}
                      {isSubmitted && selected === idx && idx !== q.correctIndex && (
                        <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                      )}
                      {opt}
                    </span>
                  </button>
                );
              })}
            </div>
            {!isSubmitted ? (
              <Button
                size="sm"
                onClick={() => handleSubmit(q.id)}
                disabled={selected === undefined || selected === null}
                className="bg-teal-700 hover:bg-teal-800 text-white"
              >
                Submit answer
              </Button>
            ) : (
              <div className={`mt-3 p-4 rounded-lg text-sm ${isCorrect ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
                <p className={`font-semibold mb-1 ${isCorrect ? "text-green-800" : "text-amber-800"}`}>
                  {isCorrect ? "Correct!" : "Not quite — see the explanation:"}
                </p>
                <p className={isCorrect ? "text-green-700" : "text-amber-700"}>{q.explanation}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CompletionBadge({ module, onReturn }: { module: CpdModule; onReturn: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="relative mb-6">
        <div className="w-28 h-28 rounded-full bg-teal-50 border-4 border-teal-200 flex items-center justify-center shadow-lg">
          <Award className="h-14 w-14 text-teal-600" />
        </div>
        <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center shadow">
          <Star className="h-4 w-4 text-white fill-white" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Module Complete!</h2>
      <p className="text-slate-600 mb-4 max-w-sm">
        You have completed <span className="font-semibold text-teal-700">{module.title}</span> and earned the
      </p>
      <div className="bg-teal-900 text-white rounded-2xl px-8 py-5 mb-6 shadow-lg">
        <div className="flex items-center gap-3 justify-center mb-1">
          <Award className="h-6 w-6 text-teal-300" />
          <span className="text-lg font-bold">{module.badgeName}</span>
        </div>
        <p className="text-teal-300 text-sm">{module.cpdPoints} CPD point{module.cpdPoints !== 1 ? "s" : ""} · {module.estimatedMinutes} minutes</p>
      </div>
      <p className="text-slate-500 text-sm mb-8 max-w-xs">
        This activity can be recorded in your CPD portfolio and used to support revalidation.
      </p>
      <Button onClick={onReturn} className="bg-teal-700 hover:bg-teal-800 text-white">
        Back to Learning Hub
      </Button>
    </div>
  );
}

export function CpdModuleReaderContent({ listPath }: { listPath: string }) {
  const params = useParams<{ id: string }>();
  const { isSignedIn } = useAuth();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [reflectionText, setReflectionText] = useState("");
  const [showCompletion, setShowCompletion] = useState(false);

  const moduleId = params.id ? parseInt(params.id, 10) : NaN;
  const validId = !isNaN(moduleId) && moduleId > 0 ? moduleId : 0;

  const {
    data: module,
    isLoading: moduleLoading,
    error: moduleQueryError,
  } = useGetModule(validId, {
    query: { queryKey: getGetModuleQueryKey(validId), enabled: validId > 0 },
  });

  const { data: allProgress = [], isLoading: progressLoading } = useGetMyModuleProgress({
    query: { queryKey: getGetMyModuleProgressQueryKey(), enabled: isSignedIn === true },
  });

  const { mutateAsync: updateProgress } = useUpdateModuleProgress();

  const progress =
    (allProgress as CpdModuleProgress[]).find((p) => p.moduleId === validId) ?? null;

  const reflectionStorageKey = module
    ? `rheumhub_reflection_${module.id}_s${module.sections[currentSectionIdx]?.id ?? currentSectionIdx}`
    : null;

  useEffect(() => {
    if (!reflectionStorageKey) return;
    const saved = localStorage.getItem(reflectionStorageKey);
    setReflectionText(saved ?? "");
  }, [reflectionStorageKey]);

  const isSectionCompleted = useCallback(
    (sectionId: number) => progress?.sectionsCompleted?.includes(sectionId) ?? false,
    [progress]
  );

  const markSectionComplete = useCallback(
    async (sectionId: number, completed: boolean) => {
      if (!isSignedIn || !module) return;
      try {
        const updated = await updateProgress({ id: module.id, data: { sectionId, completed } });
        await queryClient.invalidateQueries({ queryKey: getGetMyModuleProgressQueryKey() });
        if (
          updated.completedAt &&
          module.sections.length > 0 &&
          updated.sectionsCompleted.length >= module.sections.length
        ) {
          setShowCompletion(true);
        }
      } catch {
        // silent — progress will refresh on next query invalidation
      }
    },
    [isSignedIn, module, updateProgress, queryClient]
  );

  const loading = validId === 0 ? false : moduleLoading || (isSignedIn ? progressLoading : false);
  const error = validId === 0 ? "Invalid module" : moduleQueryError ? "Failed to load module" : null;

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-slate-500">Loading module...</div>;
  }

  if (error || !module) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <p className="text-slate-600">{error ?? "Module not found"}</p>
        <Button onClick={() => setLocation(listPath)} variant="outline">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to Learning Hub
        </Button>
      </div>
    );
  }

  const sections = module.sections;
  const completedIds = progress?.sectionsCompleted ?? [];
  const completedCount = completedIds.length;
  const totalCount = sections.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const currentSection = sections[currentSectionIdx];
  const diffCfg = difficultyConfig[module.difficulty];

  if (showCompletion) {
    return <CompletionBadge module={module} onReturn={() => setLocation(listPath)} />;
  }

  return (
    <div>
      <button
        onClick={() => setLocation(listPath)}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700 mb-6 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" /> Back to Learning Hub
      </button>

      <div className="bg-teal-900 rounded-2xl p-6 text-white mb-6">
        <div className="flex flex-wrap items-start gap-3 mb-3">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${diffCfg.class}`}>
            {diffCfg.label}
          </span>
          <span className="text-xs bg-white/10 text-teal-200 px-2.5 py-1 rounded-full border border-white/20">
            {module.topicArea}
          </span>
        </div>
        <div className="flex items-start gap-3 mb-2">
          <BookOpenCheck className="h-6 w-6 text-teal-300 shrink-0 mt-0.5" />
          <h1 className="text-xl font-bold leading-snug">{module.title}</h1>
        </div>
        <p className="text-teal-200 text-sm mb-4 leading-relaxed">{module.description}</p>
        <div className="flex flex-wrap gap-4 text-sm text-teal-300 mb-4">
          <span className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            {module.estimatedMinutes} min
          </span>
          <span className="flex items-center gap-1.5">
            <Award className="h-4 w-4" />
            {module.cpdPoints} CPD point{module.cpdPoints !== 1 ? "s" : ""}
          </span>
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" />
            {completedCount}/{totalCount} sections
          </span>
        </div>
        <Progress value={progressPct} className="h-2 bg-white/20 [&>div]:bg-teal-400" />
        <p className="text-xs text-teal-300 mt-1.5">{progressPct}% complete</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm h-fit">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-1">Sections</h3>
          <ul className="space-y-1">
            {sections.map((s, idx) => {
              const done = isSectionCompleted(s.id);
              const isCurrent = idx === currentSectionIdx;
              return (
                <li key={s.id}>
                  <button
                    onClick={() => { setCurrentSectionIdx(idx); setQuizDone(false); }}
                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all ${
                      isCurrent ? "bg-teal-50 text-teal-800 font-semibold" : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {done ? (
                      <CheckCircle2 className="h-4 w-4 text-teal-500 shrink-0" />
                    ) : (
                      <Circle className="h-4 w-4 text-slate-300 shrink-0" />
                    )}
                    <span className="flex-1 leading-tight">{s.title}</span>
                    <SectionIcon type={s.type} />
                  </button>
                </li>
              );
            })}
          </ul>

          {module.nmcStandards.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100 px-1">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Standards</p>
              <div className="flex flex-wrap gap-1">
                {module.nmcStandards.map((s) => (
                  <Badge key={s} variant="outline" className="text-xs text-slate-600">{s}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 lg:p-8">
          {currentSection && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <SectionIcon type={currentSection.type} />
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {sectionTypeConfig[currentSection.type].label}
                </span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-6">{currentSection.title}</h2>

              {currentSection.type === "reading" && (
                <ReadingContent content={currentSection.content} />
              )}

              {currentSection.type === "quiz" && currentSection.questions && (
                <div className="mb-8">
                  <p className="text-slate-600 text-sm mb-6">{currentSection.content}</p>
                  <QuizView questions={currentSection.questions} onAllAnswered={() => setQuizDone(true)} />
                </div>
              )}

              {currentSection.type === "reflection" && (
                <div className="mb-8">
                  <p className="text-slate-600 text-sm mb-4">{currentSection.content}</p>
                  {currentSection.prompt && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-5">
                      <p className="text-amber-800 text-sm font-medium mb-1">Reflection prompt</p>
                      <p className="text-amber-700 text-sm leading-relaxed">{currentSection.prompt}</p>
                    </div>
                  )}
                  <textarea
                    value={reflectionText}
                    onChange={(e) => {
                      setReflectionText(e.target.value);
                      if (reflectionStorageKey) {
                        localStorage.setItem(reflectionStorageKey, e.target.value);
                      }
                    }}
                    placeholder="Write your reflection here..."
                    className="w-full h-40 rounded-xl border border-slate-200 p-4 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                  />
                  <p className="text-xs text-slate-400 mt-1.5">
                    Your notes are saved in this browser. Copy them into your CPD portfolio for revalidation.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-slate-100 gap-4">
                <Button
                  variant="outline"
                  onClick={() => { if (currentSectionIdx > 0) { setCurrentSectionIdx((i) => i - 1); setQuizDone(false); } }}
                  disabled={currentSectionIdx === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>

                <div className="flex items-center gap-3">
                  {!isSectionCompleted(currentSection.id) ? (
                    <Button
                      className="bg-teal-700 hover:bg-teal-800 text-white"
                      onClick={() => {
                        markSectionComplete(currentSection.id, true);
                        if (currentSectionIdx < sections.length - 1) {
                          setCurrentSectionIdx((i) => i + 1);
                          setQuizDone(false);
                        }
                      }}
                      disabled={!isSignedIn || (currentSection.type === "quiz" && !quizDone)}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1.5" />
                      {currentSection.type === "quiz" && !quizDone ? "Answer all questions first" : "Mark complete & continue"}
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-teal-700 font-semibold">
                      <CheckCircle2 className="h-4 w-4" />
                      Section complete
                    </div>
                  )}

                  {currentSectionIdx < sections.length - 1 && (
                    <Button
                      variant="outline"
                      onClick={() => { setCurrentSectionIdx((i) => i + 1); setQuizDone(false); }}
                    >
                      Next <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </div>
              </div>

              {!isSignedIn && (
                <p className="text-xs text-slate-400 mt-3 text-center">
                  Sign in to track your progress and earn CPD badges.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
